import BaseObjectDirective from '../BaseObjectDirective';

/**
 * Registers a content object with the ContentObjectRegistryService.
 * @param {Object} module - The module to register the content object with.
 */
export function registerContentObject(module) {
    module.run(['ContentObjectRegistryService', function (ContentObjectRegistryService) {
        const obj = [{
            uuid: "",
            content: "",
            objectType: "table",
            type: "contentObject",
            contentReference: false,
            preferences: {
                identifier: undefined,
                data: undefined,
                includedInPDF: true,
                richTextEnabled: true,
                mergeCells: false,
                specialChars: null,
                lockDataEntry: false,
                lockTableSize: false,
                richTextEnabledCandidate: true,
                lockHeaderStyling: true,
            }
        }];
        const objProperties = {
            type: "table",
            label: 'CMS.LAYOUT-MANAGER.NEW-OBJECT.TABLE.LABEL',
            cssClass: "new-table-object",
            icon: "cms/icon-table.png",
            description: 'CMS.LAYOUT-MANAGER.NEW-OBJECT.TABLE.DESCRIPTION',
            group: {
                id: 1
            }
        };
        ContentObjectRegistryService.register(obj, objProperties);
    }]);
}

/**
 * Registers the asfTableObject directive with the given module.
 * @param {Object} module - The module to register the directive with.
 */
export function registerAsfTableObjectDirective(module) {
    module.directive('asfTableObject', function () {
        return {
            restrict: 'EA',
            replace: true,
            scope: {
                contentObject: "="
            },
            templateUrl: "/views/ib/contentobjects/table/base.html",
            controller: AsfTableObjectCtrl
        };
    });
    registerContentObject(module);
}

/**
 * Controller for the asfTableObject directive.
 * Extends BaseObjectDirective and handles table object initialization and manipulation.
 */
class AsfTableObjectCtrl extends BaseObjectDirective {
    /**
     * Constructor for AsfTableObjectCtrl.
     * @param {Object} $scope - The scope for this controller.
     * @param {Object} $injector - The AngularJS injector service.
     * @param {Object} $uibModal - The AngularUI Bootstrap modal service.
     * @param {Object} $rootScope - The root scope for this application.
     * @param {Object} $routeParams - The route parameters.
     * @param {Object} TaskDataService - The service for task data.
     * @param {Object} $signals - The signals service.
     * @param {Object} TableObjectAPIService - The API service for table objects.
     * @param {Object} $translate - The translation service.
     * @param {Object} $config - The configuration service.
     * @param {Object} $timeout - The AngularJS timeout service.
     */
    constructor($scope, $injector, $uibModal, $rootScope, $routeParams, TaskDataService, $signals, TableObjectAPIService, $translate, $config, $timeout) {
        super($injector, $scope);
        this.debugId = "table" + Math.random();

        this.registerObjectInstance('table');

        this.$scope = $scope;
        this.$rootScope = $rootScope;
        this.$signals = $signals;
        this.TableObjectAPIService = TableObjectAPIService;
        this.$timeout = $timeout;

        $scope.isCMS = $rootScope.isCMS;
        $scope.isExamExport = $config.isExamExport();
        $scope.resetDisabled = false;

        const TaskInfoService = $injector.get('TaskInfoService');
        const taskInfo = TaskInfoService.getTaskInfo();
        $scope.langISO = $rootScope.isJournal ? $translate.preferredLanguage() : taskInfo.getISOInputLanguageCode();

        this._step3signalEmitted = false;
        this._tableData = null;
        const att = $scope.contentObject.getAttachement();
        this._candidateData = att ? att.data : null;
        if (this._candidateData) {
            console.log("[TABLE] CandidateData available", this._candidateData);
        } else {
            console.log("[TABLE] CandidateData is not available");
        }
        this._preferencesClone = angular.copy($scope.contentObject.getPreferences());
        this._changeListeners = [];
        this._tableDataIsDirty = false;

        // Scope Variable
        $scope.candidatePOV = true;
        if ($scope.isCMS && !$rootScope.isPreview && !$scope.isPDFMode && !$config.isExamExport()) {
            $scope.candidatePOV = false;
        }
        this.maxRows = 15;
        this.maxCols = 15;
        this.minRows = 1;
        this.minCols = 1;
        $scope.step = 0;

        this.initPreferences();
        this.initTable(false);

        // Bind methods to the instance
        $scope.resetTableObject = this.resetTableObject.bind(this);
        $scope.resetChanges = this.resetChanges.bind(this);

        // Call initTable in CMS or Preview-Mode
        if ($scope.isCMS || $rootScope.isPreview) {
            this.initTable(false);
        }

        this.saveResponseDataDebounced = _.debounce(() => {
            console.log('[TABLE] Saving table object');
            this.saveResponseData('TableObjectDirective.saveTableObject')
        }, 1000);

    }

    /**
     * Initializes the preferences for the table object.
     */
    initPreferences() {
        const $scope = this.$scope;
        const contentObject = $scope.contentObject;
        const preferences = contentObject.getPreferences() || {};

        $scope.preferencesValues = {
            identifier: preferences.identifier,
            includedInPDF: preferences.includedInPDF ?? false,
            richTextEnabled: preferences.richTextEnabled ?? false,
            lockMergeCell: preferences.lockMergeCell ?? false,
            specialChars: preferences.specialChars ?? null,
            lockDataEntry: preferences.lockDataEntry ?? false,
            lockTableSize: preferences.lockTableSize ?? false,
            richTextEnabledCandidate: preferences.richTextEnabledCandidate ?? false,
            lockHeaderStyling: preferences.lockHeaderStyling ?? false
        };

        if ($scope.preferencesValues.identifier) {
            this.updateRegisteredName($scope.preferencesValues.identifier);
        }

        if ($scope.isCMS && !this.$rootScope.isPreview) {
            this.setupPreferencesWatcher();
        }

        this.setupPreferencesUI();
    }

    /**
     * Sets up the watcher for preferences changes.
     */
    setupPreferencesWatcher() {
        const $scope = this.$scope;
        $scope.$watch('preferencesValues', (newVal, oldVal) => {
            if (newVal === oldVal) return;

            let broadcastValue = null;

            if (newVal.identifier !== oldVal.identifier) {
                const oldId = oldVal.identifier || $scope.contentObject.getUUID();
                const newId = newVal.identifier || $scope.contentObject.getUUID();
                this.TableObjectAPIService.updateTableObject(oldId, newId);
            }

            if (newVal.lockDataEntry !== oldVal.lockDataEntry) {
                if (newVal.lockDataEntry) {
                    $scope.preferencesValues.lockTableSize = true;
                    $scope.preferencesValues.lockMergeCell = true;
                }
                broadcastValue = { readOnly: newVal.lockDataEntry };
            }

            if (newVal.richTextEnabled !== oldVal.richTextEnabled) {
                broadcastValue = { richTextEnabled: newVal.richTextEnabled };
            }

            for (const [key, value] of Object.entries($scope.preferencesValues)) {
                $scope.contentObject.setPreference(key, value);
                if (key === 'identifier') {
                    this.updateRegisteredName(value);
                }
            }

            if (broadcastValue) {
                $scope.$broadcast('tableObject_update', broadcastValue);
            }
        }, true);
    }

    /**
     * Sets up the preferences UI configuration.
     */
    setupPreferencesUI() {
        this.$scope.preferences = [
            {
                identifier: [
                    { type: 'input', name: 'Table Identifier:', valueKey: 'identifier' }
                ],
                includedInPDF: [
                    { type: 'checkbox', name: 'Included in Response PDF', valueKey: 'includedInPDF' }
                ],
            },
            {
                title: [
                    { type: 'title', name: 'Candidate View Settings', valueKey: 'CandidateViewSettings' }
                ],
                lockTableSize: [
                    { type: 'checkbox', name: 'Locked for Size:', valueKey: 'lockTableSize' }
                ],
                lockDataEntry: [
                    { type: 'checkbox', name: 'Locked for Data Entry:', valueKey: 'lockDataEntry' }
                ],
                richTextEnabledCandidate: [
                    { type: 'checkbox', name: 'Locked so no Rich Text:', valueKey: 'richTextEnabledCandidate' }
                ],
                lockMergeCell: [
                    { type: 'checkbox', name: 'Locked for Merge Cell:', valueKey: 'lockMergeCell' }
                ],
                lockHeaderStyling: [
                    { type: 'checkbox', name: 'Lock Header Styling:', valueKey: 'lockHeaderStyling' }
                ]
            },
        ];
    }

    /**
     * Updates the registered name for the table object.
     * @param {string} name - The new name to register.
     */
    updateRegisteredName(name) {
        if (!name) return;
        this.registerObjectInstanceWithName(name);
    }

    /**
     * Initializes the table with optional empty data.
     * @param {boolean} emptyTable - Whether to initialize with empty data.
     */
    initTable(emptyTable) {
        console.log(`[TABLE] Initializing table. emptyTable: ${emptyTable}`);
        const $scope = this.$scope;
        this._tableData = null;
        const emptyData = {
            step: 0,
            steps: {}
        };

        if ($scope.candidatePOV) {
            console.log('[TABLE] Candidate POV detected');
            if (!emptyTable && this._candidateData && typeof (this._candidateData.steps) !== 'undefined') {
                console.log('[TABLE] Using candidate data');
                this._tableData = angular.copy(this._candidateData);
            } else {
                console.log('[TABLE] Using content object preferences or empty data');
                this._tableData = angular.copy($scope.contentObject.getPreferences().data || emptyData);
            }
        } else {
            console.log('[TABLE] Not in candidate POV');
            if (!emptyTable && $scope.contentObject.getPreferences().data) {
                console.log('[TABLE] Using content object preferences');
                this._tableData = angular.copy($scope.contentObject.getPreferences().data);
            } else {
                console.log('[TABLE] Using empty data');
                this._tableData = angular.copy(emptyData);
            }
        }
        console.log('[TABLE] Initialized table data. Step:', this._tableData);
        $scope.step = this._tableData.step;
        console.log(`[TABLE] Current step: ${$scope.step}`);

        // Handle old data format
        if (this._tableData.locked) {
            console.log('[TABLE] Old data format detected. Handling...');
            this.handleOldDataFormat();
        }

        if (this._tableData.step === 0) {
            console.log('[TABLE] Step is 0. Moving to next step.');
            this.nextStep();
        }

        console.log('[TABLE] Registering table object with API service');
        this.TableObjectAPIService.registerTableObject(this.buildApiObject());
        console.log('[TABLE] Table initialization complete');
    }

    /**
     * Handles the old data format, updating preferences accordingly.
     */
    handleOldDataFormat() {
        const $scope = this.$scope;
        if (this.isLockedState(2) || this.isLockedState(3)) {
            $scope.preferencesValues.lockTableSize = true;
            $scope.contentObject.setPreference('lockTableSize', true);
            $scope.preferencesValues.lockMergeCell = true;
            $scope.contentObject.setPreference('lockMergeCell', true);
        }

        if (this.isLockedState(3)) {
            $scope.preferencesValues.lockDataEntry = true;
            $scope.contentObject.setPreference('lockDataEntry', true);
        }

        delete this._tableData.locked;
        if (!$scope.candidatePOV) {
            $scope.contentObject.setPreference('data', this._tableData);
        }
    }

    /**
     * Emits a signal for the sheet wait request step 3.
     */
    emitSheetWaitRequestStep3() {
        if (this._step3signalEmitted) {
            return;
        }
        this.$signals.emit(IB.Signals.SHEET_WAIT_REQUEST, this.$scope.contentObject.getUUID(), this.$scope.contentObject.getObjectType() + "_step3");
        this._step3signalEmitted = true;
    }

    /**
     * Saves the table object.
     */
    saveTableObject() {
        if (!this.$scope.candidatePOV) {
            this.$scope.contentObject.setPreference('data', this._tableData);
        } else {
            this._tableDataIsDirty = true;
            this.saveResponseDataDebounced('TableObjectDirective.saveTableObject');
        }
    }

    /**
     * Gets the step data for the table.
     * @param {number} step - The step number.
     * @returns {Object} - The step data.
     */
    getStepData(step) {
        if (!this._tableData || !this._tableData.steps) {
            return undefined;
        }
        return step ? this._tableData.steps[step] : this._tableData.steps;
    }

    /**
     * Sets the step data for the table.
     * @param {number} step - The step number.
     * @param {Object} data - The data to set.
     */
    setStepData(step, data) {
        if (!this._tableData.steps[step]) {
            this._tableData.steps[step] = {};
        }
        if (step === 3) {
            this.setTableData(data);
        } else {
            this._tableData.steps[step] = data;
            this.saveTableObject();
            this.triggerChangeListeners(step, data);
        }
    }

    /**
     * Gets the table data.
     * @returns {Object} - The table data.
     */
    getTableData() {
        const step = 3;
        return this._tableData.steps[step]?.tableData ?? null;
    }

    /**
     * Sets the table data.
     * @param {Object} data - The data to set.
     */
    setTableData(data) {
        const step = 3;
        if (!this._tableData.steps[step]) {
            this._tableData.steps[step] = {};
        }
        this._tableData.steps[step].tableData = data;
        this.saveTableObject();
        this.triggerChangeListeners(step, data);
    }

    /**
     * Checks if a step is in a locked state.
     * @param {number} step - The step number.
     * @returns {boolean} - True if the step is locked, false otherwise.
     */
    isLockedState(step) {
        if (!this._tableData || !this._tableData.locked) return false;
        return this._tableData.locked[step] ?? false;
    }

    /**
     * Retrieves the table alignment.
     * @returns {string|null} - The alignment of the table.
     */
    getTableAlignment() {
        const step = 1;
        return this._tableData.steps[step]?.alignment ?? null;
    }

    /**
     * Sets the table alignment.
     * @param {string} data - The alignment data ('vertical' or 'horizontal').
     */
    setTableAlignment(data) {
        const step = 1;
        if (!this._tableData.steps[step]) {
            this._tableData.steps[step] = {};
        }
        if (data === 'vertical' || data === 'horizontal') {
            this._tableData.steps[step].alignment = data;
        }
        this.saveTableObject();
        this.$scope.$broadcast('tableObject_reset');
    }

    /**
     * Sets the MCQ data for the table.
     * @param {string} key - The key for the MCQ data.
     * @param {string} name - The name associated with the MCQ data.
     * @param {string} value - The value of the MCQ data.
     * @param {string} type - The type of the MCQ data.
     * @param {boolean} state - The state of the MCQ data.
     */
    setMCQData(key, name, value, type, state) {
        const step = 3;
        if (!this._tableData.steps[step]) {
            this._tableData.steps[step] = {};
        }
        if (!this._tableData.steps[step]['mcqData']) {
            this._tableData.steps[step]['mcqData'] = {};
        }
        this._tableData.steps[step]['mcqData'][key] = { name, value, type, state };
        this.saveTableObject();
    }

    /**
     * Retrieves the MCQ data for the table.
     * @returns {Object|null} - The MCQ data.
     */
    getMCQData() {
        const step = 3;
        return this._tableData.steps[step]?.mcqData ?? null;
    }

    /**
     * Sets the merge cells data for the table.
     * @param {Object} data - The merge cells data.
     */
    setMergeCells(data) {
        const step = 3;
        if (!this._tableData.steps[step]) {
            this._tableData.steps[step] = {};
        }
        this._tableData.steps[step]['mergeCells'] = data;
        this.saveTableObject();
    }

    /**
     * Retrieves the merge cells data for the table.
     * @returns {Object|null} - The merge cells data.
     */
    getMergeCells() {
        const step = 3;
        return this._tableData.steps[step]?.mergeCells ?? null;
    }

    /**
     * Increments the step to the next one if it is less than 3.
     */
    nextStep() {
        if (this._tableData.step < 3) {
            this._tableData.step++;
            this.$scope.step = this._tableData.step;
        }
        this.saveTableObject();
    }

    /**
     * Registers a change listener.
     * @param {Function} cb - The callback function to be registered as a change listener.
     */
    registerChangeListener(cb) {
        if (typeof cb === 'function') {
            this._changeListeners.push(cb);
        }
    }

    /**
     * Triggers all registered change listeners.
     * @param {number} step - The step number.
     * @param {*} data - The data associated with the step.
     */
    triggerChangeListeners(step, data) {
        this._changeListeners.forEach(cb => cb(step, data));
    }

    /**
     * Resets the table object.
     */
    resetTableObject() {
        if (this.$scope.candidatePOV) {
            this.initTable(true);
            this.$scope.$broadcast('tableObject_reset');
            this._tableDataIsDirty = true;
        } else {
            this.initTable(true);
        }
    }

    /**
     * Resets the changes made to the table object preferences and data.
     */
    resetChanges() {
        // Restore preferences from the cloned preferences
        for (const key in this._preferencesClone) {
            if (Object.prototype.hasOwnProperty.call(this._preferencesClone, key)) {
                const value = this._preferencesClone[key];
                this.$scope.contentObject.setPreference(key, angular.copy(value));
                this.$scope.preferencesValues[key] = angular.copy(value);
            }
        }
        this.initTable(false);
        this.$scope.$broadcast('tableObject_reset');
    }

    /**
     * Disables the reset button.
     */
    disabledResetBtn() {
        this.$timeout(() => {
            this.$scope.resetDisabled = true;
        });
    }

    /**
     * Enables the reset button.
     */
    enabledResetBtn() {
        this.$timeout(() => {
            this.$scope.resetDisabled = false;
        });
    }

    /**
     * Called by the BaseObjectDirective.responseFill() method. 
     * This is called when the response is being filled into question sheet the candidate returns to.
     * @param {Object} attachement the current attachement data
     */
    responseFill(attachement) {
        if (attachement && attachement.data) {
            this._candidateData = attachement.data;
        }
        this.initTable(false);
        if (this.$scope.isPDFMode) {
            if (this.$scope.step == 3) {
                this.emitSheetWaitRequestStep3();
            }
            this.$scope.step = 3;
        }
    }

    /**
    * This is called by BaseObjectDirective.responseCollect() method.
    * This is called when the response for the current object is being collected in order to save/persist it.
    * @param {*} attachement the current attachement data
    * @param {*} triggeredBy the event that triggered the collection
    * @returns true if the attachement has changed, false otherwise (essentially if the attachement is "dirty")
    */
    async responseCollect(attachement, triggeredBy) {

        attachement.step = this.$scope.step;
        if (this._tableDataIsDirty && this._tableData) {
            const tableDataDiffers = JSON.stringify(attachement.data) !== JSON.stringify(this._tableData);

            console.log('[TABLE] responseCollect', attachement, triggeredBy, tableDataDiffers);
            if (!tableDataDiffers) {
                return false;
            }
            attachement.data = angular.copy(this._tableData);
            return true;
        }
        return false;
    }

    /**
     * Builds the API object for the table.
     * @returns {Object} - The API object.
     */
    buildApiObject() {
        const apiObject = {};

        if (this.getStepData(2)) {
            apiObject.rows = this.getStepData(2).rows;
            apiObject.cols = this.getStepData(2).cols;
        }

        apiObject.id = this.$scope.preferencesValues.identifier || this.$scope.contentObject.getUUID();
        apiObject.maxRows = this.maxRows;
        apiObject.maxCols = this.maxCols;

        apiObject.readCell = (row, col) => {
            const tableData = this.getTableData();
            if (tableData) {
                const cell = tableData.find(cell => cell.row === row && cell.col === col);
                return cell ? cell.data : undefined;
            }
            return undefined;
        };

        apiObject.writeCell = (row, col, value) => {
            const tableData = this.getTableData();
            if (tableData) {
                let cell = tableData.find(cell => cell.row === row && cell.col === col);
                if (cell) {
                    if (cell.readOnly) {
                        console.warn(`[TABLE] Cell (${row}, ${col}) is read-only!`);
                        return false;
                    }
                    cell.data = value;
                } else {
                    tableData.push({
                        row,
                        col,
                        data: value,
                        readOnly: false
                    });
                }
                this.setTableData(tableData);
                this.$scope.$broadcast('tableObject_update', 'updateData');
                return true;
            }
            return false;
        };

        apiObject.setCellReadOnly = (row, col, value) => {
            const tableData = this.getTableData();
            if (tableData) {
                let cell = tableData.find(cell => cell.row === row && cell.col === col);
                if (cell) {
                    cell.readOnly = value;
                } else {
                    tableData.push({
                        row,
                        col,
                        readOnly: value
                    });
                }
                this.setTableData(tableData);
                this.$scope.$broadcast('tableObject_update', 'updateData');
                return true;
            }
            return false;
        };

        apiObject.addRow = () => {
            const data = this.getStepData(2);
            if (data) {
                if (data.rows && data.rows >= this.maxRows) {
                    return false;
                }
                this.$scope.$broadcast('tableObject_update', { action: 'addRow' });
                return true;
            }
            return false;
        };

        apiObject.removeRow = () => {
            const data = this.getStepData(2);
            const tableAlignment = this.getTableAlignment();
            const minRows = tableAlignment === 'horizontal' ? 2 : 1;

            if (data) {
                if (data.rows && data.rows <= minRows) {
                    return false;
                }
                this.$scope.$broadcast('tableObject_update', { action: 'removeRow' });
                return true;
            }
            return false;
        };

        apiObject.addCol = () => {
            const data = this.getStepData(2);
            if (data) {
                if (data.cols && data.cols >= this.maxCols) {
                    return false;
                }
                this.$scope.$broadcast('tableObject_update', { action: 'addCol' });
                return true;
            }
            return false;
        };

        apiObject.removeCol = () => {
            const data = this.getStepData(2);
            if (data) {
                if (data.cols && data.cols <= 1) {
                    return false;
                }
                this.$scope.$broadcast('tableObject_update', { action: 'removeCol' });
                return true;
            }
            return false;
        };

        return apiObject;
    }
}
